// src/pages/ProductDetails/BatteryLifeComparison.jsx
import React, { useEffect, useState, useRef } from "react";
import "./InverterShared.css";
import "./BatteryLifeComparison.css";

const BatteryLifeComparison = ({ productImage }) => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [lithiumCycles, setLithiumCycles] = useState(0);
  const [leadCycles, setLeadCycles] = useState(0);

  const LEAD_ACID_IMG =
    "https://res.cloudinary.com/dhbjxspxa/image/upload/v1777475751/products/Lead-Acid-Battery_new_ybhym4.jpg";
  const FALLBACK_LITHIUM =
    "https://placehold.co/400x400/f5f5f7/333?text=Zynventics+Integrated";

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 },
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    const animateValue = (setFn, end, duration) => {
      let start = 0;
      const incrementTime = 20;
      const steps = Math.ceil(duration / incrementTime);
      const stepValue = end / steps;

      const timer = setInterval(() => {
        start += stepValue;
        if (start >= end) {
          setFn(end);
          clearInterval(timer);
        } else {
          setFn(Math.floor(start));
        }
      }, incrementTime);
    };

    animateValue(setLithiumCycles, 2000, 1500);
    animateValue(setLeadCycles, 400, 1000);
  }, [isVisible]);

  return (
    <section className="apple-section lifecycle-section" ref={sectionRef}>
      <div className="lifecycle-header">
        <h2 className="apple-title">The Built-In Lithium Advantage</h2>
        <p className="apple-subtitle">
          Discover why our integrated plug-and-play technology completely
          outclasses traditional, bulky battery setups.
        </p>
      </div>

      <div className={`lifecycle-container ${isVisible ? "is-visible" : ""}`}>
        {/* =========================================
            LEFT COLUMN: ZYNVENTICS INTEGRATED SYSTEM
            ========================================= */}
        <div className="battery-column lithium-col">
          <div className="badge winner-badge">All-In-One Solution</div>

          <div className="img-scale-wrapper">
            <img
              src={productImage || FALLBACK_LITHIUM}
              alt="Zynventics Integrated Inverter"
              className="battery-hero-img zyn-img"
            />
          </div>

          <h3 className="battery-title">Zynventics Integrated System</h3>

          {/* Setup & Maintenance Box */}
          <div className="maintenance-feature-box">
            <div className="m-feature">
              <span className="m-icon animate-float">🔌</span>
              <div className="m-text">
                <strong>Plug & Forget</strong>
                <span>Zero maintenance required. Ever.</span>
              </div>
            </div>
            <div className="m-feature">
              <span className="m-icon">📦</span>
              <div className="m-text">
                <strong>All-In-One Unit</strong>
                <span>Inverter + Battery inside one sleek box.</span>
              </div>
            </div>
            <div className="m-feature">
              <span className="m-icon">🪶</span>
              <div className="m-text">
                <strong>Lightweight</strong>
                <span>Wall-mountable & easy to move (~15kg).</span>
              </div>
            </div>
          </div>

          {/* Stats Box */}
          <div className="stats-box">
            <div className="stat-row">
              <span className="stat-label">Lifespan</span>
              <span className="stat-value highlight-blue">
                {lithiumCycles}+ <span className="stat-sub">Cycles</span>
              </span>
            </div>
            <p className="stat-desc">
              Internal battery lasts 7 to 10 years easily.
            </p>
          </div>

          <div className="dod-box">
            <div className="dod-header">
              <span>Depth of Discharge (DoD)</span>
              <strong>100% Usable Power</strong>
            </div>
            <div className="dod-battery-visual">
              <div className="dod-fill lithium-fill"></div>
              <span className="dod-text">Safe to drain completely</span>
            </div>
          </div>
        </div>

        {/* CENTER DIVIDER */}
        <div className="lifecycle-vs-divider">
          <div className="vs-line"></div>
          <span className="vs-circle">VS</span>
          <div className="vs-line"></div>
        </div>

        {/* =========================================
            RIGHT COLUMN: TRADITIONAL SETUP
            ========================================= */}
        <div className="battery-column lead-col">
          <div className="badge outdated-badge">Outdated</div>

          <div className="img-scale-wrapper">
            <img
              src={LEAD_ACID_IMG}
              alt="Traditional Lead Acid Setup"
              className="battery-hero-img lead-img"
            />
          </div>

          <h3 className="battery-title">Traditional Setup</h3>

          {/* Setup & Maintenance Box */}
          <div className="maintenance-feature-box">
            <div className="m-feature">
              <span className="m-icon water-drop-container">
                <span className="drop">💧</span>
              </span>
              <div className="m-text">
                <strong>High Maintenance</strong>
                <span>Requires Acid/Water top-up every 6 months.</span>
              </div>
            </div>
            <div className="m-feature">
              <span className="m-icon">⚠️</span>
              <div className="m-text">
                <strong>Clunky Setup</strong>
                <span>
                  Requires external inverter, external battery, and messy wires.
                </span>
              </div>
            </div>
            <div className="m-feature">
              <span className="m-icon animate-heavy">🏋️‍♂️</span>
              <div className="m-text">
                <strong>Extremely Heavy</strong>
                <span>Requires a trolley. Extremely hard to move (60kg+).</span>
              </div>
            </div>
          </div>

          {/* Stats Box */}
          <div className="stats-box">
            <div className="stat-row">
              <span className="stat-label">Lifespan</span>
              <span className="stat-value highlight-red">
                {leadCycles} <span className="stat-sub">Cycles</span>
              </span>
            </div>
            <p className="stat-desc">
              Requires messy replacements every 2-3 years.
            </p>
          </div>

          <div className="dod-box">
            <div className="dod-header">
              <span>Depth of Discharge (DoD)</span>
              <strong>50% Usable Power</strong>
            </div>
            <div className="dod-battery-visual">
              <div className="dod-fill lead-fill"></div>
              <div className="dod-damage-zone">
                <span>Damage Zone</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={`dod-explanation ${isVisible ? "is-visible" : ""}`}>
        <h4>The Secret Behind the Compact Size</h4>
        <p>
          <strong>Depth of Discharge (DoD)</strong> is how much energy you can
          actually extract. A traditional lead-acid setup permanently damages
          itself if drained below 50% capacity. Because our{" "}
          <strong>integrated lithium technology supports 100% DoD</strong>, we
          can fit the exact same usable power into a compact, sleek wall-mounted
          unit—saving you space, money, and maintenance hassles.
        </p>
      </div>
    </section>
  );
};

export default BatteryLifeComparison;
